import { useEffect, useState, useRef, type MouseEvent } from 'react'
import { Link, useParams } from 'react-router-dom'
import { postsApi, mediaUrl, type Post } from '@/lib/api'
import { applyMeta, postPageMeta } from '@/lib/seo'
import ArticleRichText, { extractHeadings, readingTime } from '@/atz/ArticleRichText'
import NotFound from '@/atz/pages/NotFound'
import './BlogPost.css'

const Icon = ({ id }: { id: string }) => (
  <svg className="ico">
    <use href={`#${id}`} />
  </svg>
)

declare global {
  interface Window {
    __ATZ_POST__?: Post
  }
}

// The server embeds the post it rendered so the first paint needs no fetch.
const embeddedPost = (slug?: string): Post | undefined => {
  const p = window.__ATZ_POST__
  return p && p.slug === slug ? p : undefined
}

const fmtDate = (iso?: string) =>
  iso
    ? new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : ''

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>()
  const [post, setPost] = useState<Post | null | undefined>(() => embeddedPost(slug))
  const [related, setRelated] = useState<Post[]>([])
  const [activeId, setActiveId] = useState('')
  const [copied, setCopied] = useState(false)
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!slug) return
    const embedded = embeddedPost(slug)
    if (embedded) {
      setPost(embedded)
      return
    }
    setPost(undefined)
    postsApi.bySlug(slug).then(setPost).catch(() => setPost(null))
  }, [slug])

  useEffect(() => {
    if (slug && post) applyMeta(postPageMeta(post), `/${slug}/`)
  }, [slug, post])

  useEffect(() => {
    postsApi
      .list(12)
      .then((res) => setRelated(res.docs))
      .catch(() => {})
  }, [])

  const toc = post ? extractHeadings(post.content) : []

  useEffect(() => {
    if (!toc.length) return
    const onScroll = () => {
      const pos = window.scrollY + 120
      let cur = toc[0].id
      toc.forEach((h) => {
        const el = document.getElementById(h.id)
        if (el && el.offsetTop <= pos) cur = h.id
      })
      setActiveId(cur)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post])

  if (post === undefined) {
    return (
      <main id="top">
        <section className="post-head">
          <div className="wrap-wide">
            <p style={{ color: 'var(--muted)' }}>Loading…</p>
          </div>
        </section>
      </main>
    )
  }

  if (post === null) return <NotFound />

  const authorName = typeof post.author === 'object' ? post.author.name : undefined
  const firstTag = post.tags && post.tags.length > 0 ? post.tags[0].tag : undefined
  const category = typeof post.primaryCategory === 'object' ? post.primaryCategory.name : undefined
  const rt = readingTime(post.content)
  const others = related.filter((p) => p.slug !== post.slug).slice(0, 3)

  const pageUrl = window.location.href

  const onNativeShare = async (e: MouseEvent) => {
    e.preventDefault()
    if (navigator.share) {
      await navigator.share({ title: post.title, url: pageUrl })
    } else {
      await navigator.clipboard.writeText(pageUrl)
      if (copyTimer.current) clearTimeout(copyTimer.current)
      setCopied(true)
      copyTimer.current = setTimeout(() => setCopied(false), 2000)
    }
  }

  const onTocClick = (e: MouseEvent, id: string) => {
    e.preventDefault()
    const el = document.getElementById(id)
    if (el) window.scrollTo({ top: el.offsetTop - 96, behavior: 'smooth' })
  }

  return (
    <main id="top">
      <section className="post-head">
        <div className="wrap-wide">
          <nav className="crumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span className="sep">/</span>
            <Link to="/blogs/">Blogs</Link>
            {category && (
              <>
                <span className="sep">/</span>
                <span>{category}</span>
              </>
            )}
            <span className="sep">/</span>
            <b>{post.title}</b>
          </nav>
          {(category || firstTag) && <span className="pill">{category || firstTag}</span>}
          <h1>{post.title}</h1>
          <div className="post-meta">
            {authorName && (
              <span>
                <Icon id="i-user" /> {authorName}
              </span>
            )}
            {post.publishedAt && (
              <span>
                <Icon id="i-cal" /> {fmtDate(post.publishedAt)}
              </span>
            )}
            {rt > 0 && (
              <span>
                <Icon id="i-clock" /> {rt} min read
              </span>
            )}
            {firstTag && (
              <span>
                <Icon id="i-tag" /> {firstTag}
              </span>
            )}
          </div>
          <div className="post-cover">
            {post.coverImage ? (
              <img
                src={mediaUrl(post.coverImage, 'hero')}
                alt={post.coverImage.alt || post.title}
                style={{
                  width: '100%',
                  aspectRatio: '16 / 6.4',
                  objectFit: 'cover',
                  borderRadius: '18px',
                }}
              />
            ) : (
              <div className="ph" data-label="ATZ Solution" />
            )}
          </div>
        </div>
      </section>

      <section>
        <div className="wrap-wide">
          <div className="post-wrap">
            <aside className="post-cta">
              <div className="cta-card">
                <span className="seal">
                  <span>A</span>
                </span>
                <h3>Need help registering?</h3>
                <p>
                  Skip the guesswork. Our specialists prepare and file your federal registrations
                  correctly the first time.
                </p>
                <Link className="btn btn-red" to="/contact-us/">
                  Book a Free Consultation <span className="arw">→</span>
                </Link>
                <div className="ph-note">100% satisfaction · no obligation</div>
              </div>
              <div className="share">
                <div className="lbl">Share this article</div>
                <div className="row">
                  <a href="#" onClick={onNativeShare} aria-label={copied ? 'Link copied!' : 'Copy link'} title={copied ? 'Link copied!' : 'Copy link'}>
                    <Icon id={copied ? 'i-check' : 'i-share'} />
                  </a>
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`}
                    target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook"
                  >
                    <Icon id="i-facebook" />
                  </a>
                  <a
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`}
                    target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn"
                  >
                    <Icon id="i-linkedin" />
                  </a>
                  <a
                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(post.title)}`}
                    target="_blank" rel="noopener noreferrer" aria-label="Share on X"
                  >
                    <Icon id="i-xcom" />
                  </a>
                </div>
              </div>
            </aside>

            <article className="article">
              {post.excerpt && <p className="lead">{post.excerpt}</p>}
              <ArticleRichText content={post.content} />
            </article>

            {toc.length > 0 && (
              <aside className="post-toc">
                <div className="hd">
                  <Icon id="i-list" /> On this page
                </div>
                <ul id="toc">
                  {toc.map((h) => (
                    <li key={h.id}>
                      <a
                        href={`#${h.id}`}
                        className={activeId === h.id ? 'active' : ''}
                        onClick={(e) => onTocClick(e, h.id)}
                      >
                        {h.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </aside>
            )}
          </div>
        </div>
      </section>

      {others.length > 0 && (
        <section className="related">
          <div className="wrap-wide">
            <div className="hd">
              <span className="eyebrow center">Keep reading</span>
              <h2>Read more from the blog</h2>
            </div>
            <div className="rel-grid">
              {others.map((p) => {
                const prt = readingTime(p.content)
                return (
                  <Link key={p.id} className="rel-card" to={`/${p.slug}/`}>
                    <div className="media">
                      {p.coverImage ? (
                        <img
                          src={mediaUrl(p.coverImage, 'card')}
                          alt={p.coverImage.alt || p.title}
                          style={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      ) : (
                        <div className="ph" data-label="ATZ Solution" />
                      )}
                    </div>
                    <div className="body">
                      <h3>{p.title}</h3>
                      <div className="foot">
                        <span>{fmtDate(p.publishedAt)}</span>
                        {prt > 0 && <span className="rd">{prt} min read</span>}
                      </div>
                    </div>
                  </Link>
                )
              })}
            </div>
            <div style={{ textAlign: 'center', marginTop: '42px' }}>
              <Link className="btn btn-primary" to="/blogs/">
                View All Articles <span className="arw">→</span>
              </Link>
            </div>
          </div>
        </section>
      )}
    </main>
  )
}
